import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../api/api';
import styles from './CompletarRegistro.module.css';
import { completarRegistroSchema } from '../lib/validations';
import { useToast } from '../Components/ui/Toast.jsx';

const CompletarRegistro = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError]   = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const [currentUser, setCurrentUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  // Verificar sesión con el backend (cookie HttpOnly) y obtener datos básicos del usuario
  useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      try {
        const meRes = await api.get('/me');
        const userRes = await api.get(`/user/${meRes.data.id}`);
        if (!cancelled) {
          setCurrentUser({ user: { id: meRes.data.id, name: userRes.data.Name, completo: userRes.data.completo } });
          setSessionChecked(true);
        }
      } catch {
        if (!cancelled) {
          setSessionChecked(true);
          showToast('Debes iniciar sesión para completar tu perfil.', { type: 'warning' });
          navigate('/');
        }
      }
    };

    loadSession();
    return () => { cancelled = true; };
  }, [navigate, showToast]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(completarRegistroSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      tipoDoc:     'Cédula',
      telefonoPais: '+58',
    },
  });

  // Bloquear teclas no numéricas en el campo de teléfono
  const blockNonDigits = (e) => {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!allowed.includes(e.key) && !/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const onSubmit = async (data) => {
    if (!currentUser?.user?.id) return;
    setServerError('');

    if (!captchaToken) {
      setServerError('Por favor completa el CAPTCHA.');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/complete_profile', {
        id:             currentUser.user.id,
        identification: `${data.tipoDoc}:${data.documento}`,
        address:        data.direccion.trim(),
        tel_number:     `${data.telefonoPais}${data.telefono}`,
      });

      showToast('Registro completado con éxito', { type: 'success' });
      navigate('/PerfilUsuario');
    } catch (err) {
      const msg =
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.error ||
        'Error al guardar los datos. Intenta nuevamente.';
      setServerError(msg);
    } finally {
      setIsSubmitting(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  // No renderizar nada hasta confirmar sesión (el useEffect redirigirá)
  if (!sessionChecked || !currentUser?.user?.id) {
    return <div className={styles.container}><p>Validando sesión...</p></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.formCard}>
        <h2>Completa tu registro</h2>
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '14px', marginTop: 0, marginBottom: '20px' }}>
          Necesitamos algunos datos adicionales para finalizar tu cuenta.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.form}>

          {/* TIPO DE DOCUMENTO */}
          <label className={styles.fieldLabel}>
            Tipo de Documento
            <select
              className={errors.tipoDoc ? styles.selectError : ''}
              {...register('tipoDoc')}
            >
              <option value="Cédula">Cédula</option>
              <option value="Pasaporte">Pasaporte</option>
              <option value="RUT">RUT</option>
              <option value="Otro">Otro</option>
            </select>
            {errors.tipoDoc && (
              <span className={styles.fieldError} role="alert">{errors.tipoDoc.message}</span>
            )}
          </label>

          {/* NÚMERO DE DOCUMENTO */}
          <label className={styles.fieldLabel}>
            Número de Documento
            <input
              type="text"
              inputMode="numeric"
              placeholder="Ej: 12345678"
              className={errors.documento ? styles.inputError : ''}
              aria-describedby={errors.documento ? 'doc-error' : undefined}
              aria-invalid={errors.documento ? 'true' : undefined}
              onKeyDown={blockNonDigits}
              {...register('documento')}
            />
            {errors.documento && (
              <span id="doc-error" className={styles.fieldError} role="alert">{errors.documento.message}</span>
            )}
          </label>

          {/* DIRECCIÓN */}
          <label className={styles.fieldLabel}>
            Dirección
            <input
              type="text"
              placeholder="Tu dirección de residencia"
              className={errors.direccion ? styles.inputError : ''}
              aria-describedby={errors.direccion ? 'dir-error' : undefined}
              aria-invalid={errors.direccion ? 'true' : undefined}
              {...register('direccion')}
            />
            {errors.direccion && (
              <span id="dir-error" className={styles.fieldError} role="alert">{errors.direccion.message}</span>
            )}
          </label>

          {/* TELÉFONO */}
          <label className={styles.fieldLabel}>
            Teléfono
            <div className={styles.telefono}>
              <select {...register('telefonoPais')}>
                <option value="+58">+58 Venezuela</option>
                <option value="+57">+57 Colombia</option>
                <option value="+1">+1 EEUU</option>
                <option value="+54">+54 Argentina</option>
              </select>
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Número de teléfono"
                  className={errors.telefono ? styles.inputError : ''}
                  aria-describedby={errors.telefono ? 'tel-error' : undefined}
                  aria-invalid={errors.telefono ? 'true' : undefined}
                  onKeyDown={blockNonDigits}
                  {...register('telefono')}
                />
                {errors.telefono && (
                  <span id="tel-error" className={styles.fieldError} role="alert">{errors.telefono.message}</span>
                )}
              </div>
            </div>
          </label>

          {/* Error de servidor */}
          {serverError && (
            <p className={styles.serverError}>{serverError}</p>
          )}

          {/* CAPTCHA */}
          <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
            />
          </div>

          <button type="submit" disabled={isSubmitting || !captchaToken}>
            {isSubmitting ? 'Guardando...' : 'Guardar y continuar'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompletarRegistro;
